import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, type FormEvent } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { adminApi } from "../../api/admin";
import type { AdminDeviceRow } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { formatDateTime, utcTooltip } from "../../shared/format";
import { roleAtLeast, type StaffRole } from "../../shared/hooks/useStaff";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  Modal,
  Spinner,
} from "../../ui";
import { DeviceActions } from "./DeviceActions";
import { Pager, Select, stateTone, useCursorPages } from "./shared";

export function DevicesPage() {
  const { role, product } = useOutletContext<{
    role: StaffRole;
    product: string | null;
  }>();
  const canMutate = roleAtLeast(role, "admin");
  const queryClient = useQueryClient();
  const EMPTY = { org: "", product: "", status: "" };
  const [draft, setDraft] = useState(EMPTY);
  const [filters, setFilters] = useState(EMPTY);
  const pages = useCursorPages();
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const [confirm, setConfirm] = useState<{ id: string; name: string } | null>(
    null,
  );
  const query = useQuery({
    queryKey: ["admin", "devices", filters, pages.cursor],
    queryFn: () =>
      adminApi.devices({
        ...filters,
        product: product ?? filters.product,
        cursor: pages.cursor,
      }),
  });
  const act = (id: string, action: "revoke" | "disable" | "enable") =>
    adminApi
      .deviceAction(id, action)
      .then((d) => {
        setMessage({
          kind: "success",
          text: `Device ${d.name} is now ${d.status}.`,
        });
        setConfirm(null);
        return queryClient.invalidateQueries({
          queryKey: ["admin", "devices"],
        });
      })
      .catch((err) => {
        setMessage({ kind: "error", text: errorMessage(err) });
        setConfirm(null);
      });
  const columns: ColumnDef<AdminDeviceRow, unknown>[] = [
    { accessorKey: "name", header: "Name" },
    {
      id: "org",
      header: "Org",
      cell: ({ row }) => (
        <Link
          className="text-brand underline"
          to={`/admin/orgs/${row.original.org_id}`}
        >
          {row.original.org_name ?? row.original.org_id}
        </Link>
      ),
    },
    { accessorKey: "product", header: "Product" },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge tone={stateTone(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      id: "last_seen_at",
      header: "Last seen",
      cell: ({ row }) => (
        <span title={utcTooltip(row.original.last_seen_at)}>
          {formatDateTime(row.original.last_seen_at)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <DeviceActions
          device={row.original}
          canMutate={canMutate}
          onAction={(id, action) =>
            action === "revoke"
              ? setConfirm({ id, name: row.original.name })
              : void act(id, action)
          }
        />
      ),
    },
  ];
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFilters(draft);
    pages.reset();
  };
  return (
    <Card>
      <h1 className="mb-4 text-xl font-semibold">Devices</h1>
      <form
        onSubmit={submit}
        className="mb-4 grid gap-3 md:grid-cols-4"
        aria-label="Filter devices"
      >
        <Input
          label="Org id"
          value={draft.org}
          onChange={(e) => setDraft({ ...draft, org: e.target.value })}
        />
        <Input
          label="Product"
          value={draft.product}
          onChange={(e) => setDraft({ ...draft, product: e.target.value })}
        />
        <Select
          label="Status"
          value={draft.status}
          onChange={(status) => setDraft({ ...draft, status })}
          options={[
            { value: "", label: "any" },
            ...["active", "disabled", "revoked"].map((s) => ({
              value: s,
              label: s,
            })),
          ]}
        />
        <div className="flex items-end">
          <Button type="submit">Filter</Button>
        </div>
      </form>
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      {query.isLoading ? <Spinner /> : null}
      {query.data ? (
        <DataTable<AdminDeviceRow>
          columns={columns}
          data={query.data.items}
          emptyMessage="No devices match."
        />
      ) : null}
      <Pager pages={pages} nextCursor={query.data?.next_cursor} />
      <Modal
        open={confirm !== null}
        title={`Revoke ${confirm?.name ?? "device"}?`}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => confirm && void act(confirm.id, "revoke")}
            >
              Confirm
            </Button>
          </>
        }
      >
        The device stops working within 15 minutes and must be enrolled again.
      </Modal>
    </Card>
  );
}
